import { baseApi } from '@services/api/baseApi';
import {
  invalidateTagsOnSuccess,
  invalidateTagsOnSuccessConst,
  providesPaginatedListTags,
  providesTagsOnSuccess,
} from '@services/api/rtkqTagHelpers';
import { type ListQuery, listQueryToParams, type ResponseDataList } from '@shared/api/list';

import type { ManagedProjectClient } from './types';

export interface CreateProjectClientRequest {
  name: string;
  externalReference?: string;
  contactName?: string;
  contactEmail?: string;
  contactPhone?: string;
  address?: string;
  defaultDiscountPercent?: number;
  active: boolean;
}

export type UpdateProjectClientRequest = CreateProjectClientRequest & { id: string };

const projectClientsApiBase = baseApi.enhanceEndpoints({ addTagTypes: ['ProjectClient'] });

export const projectClientsApi = projectClientsApiBase.injectEndpoints({
  endpoints: (build) => ({
    listProjectClients: build.query<ResponseDataList<ManagedProjectClient>, ListQuery>({
      query: (q) => ({ url: 'project-clients', params: listQueryToParams(q) }),
      providesTags: providesPaginatedListTags('ProjectClient'),
    }),
    getProjectClient: build.query<ManagedProjectClient, string>({
      query: (id) => ({ url: `project-clients/${id}` }),
      providesTags: providesTagsOnSuccess((_result, id) => [{ type: 'ProjectClient' as const, id }]),
    }),
    createProjectClient: build.mutation<ManagedProjectClient, CreateProjectClientRequest>({
      query: (body) => ({ url: 'project-clients', method: 'POST', body }),
      invalidatesTags: invalidateTagsOnSuccessConst([{ type: 'ProjectClient', id: 'LIST' }]),
    }),
    updateProjectClient: build.mutation<void, UpdateProjectClientRequest>({
      query: ({ id, ...body }) => ({ url: `project-clients/${id}`, method: 'PUT', body }),
      invalidatesTags: invalidateTagsOnSuccess((_result, arg) => [
        { type: 'ProjectClient', id: arg.id },
        { type: 'ProjectClient', id: 'LIST' },
      ]),
    }),
    deleteProjectClient: build.mutation<void, string>({
      query: (id) => ({ url: `project-clients/${id}`, method: 'DELETE' }),
      invalidatesTags: invalidateTagsOnSuccess((_result, id) => [
        { type: 'ProjectClient', id },
        { type: 'ProjectClient', id: 'LIST' },
      ]),
    }),
  }),
});

export const {
  useListProjectClientsQuery,
  useLazyListProjectClientsQuery,
  useGetProjectClientQuery,
  useLazyGetProjectClientQuery,
  useCreateProjectClientMutation,
  useUpdateProjectClientMutation,
  useDeleteProjectClientMutation,
} = projectClientsApi;
